// apps/client/src/services/socket.service.ts

import { io } from "socket.io-client";
import type { Socket } from "socket.io-client";
import type {
  ServerToClientEvents,
  ClientToServerEvents,
  NotificationServerToClientEvents,
  NotificationClientToServerEvents,
} from "@yogo/shared";

const SOCKET_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

// ─── Type aliases (avoids JSX generic parsing issues) ────────────────────────
type TrackingSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
type NotifSocket = Socket<
  NotificationServerToClientEvents,
  NotificationClientToServerEvents
>;

// ─── Tracking Socket ──────────────────────────────────────────────────────────
let trackingSocket: TrackingSocket | null = null;

export function connectTrackingSocket(token: string): TrackingSocket {
  if (trackingSocket?.connected) return trackingSocket;

  trackingSocket = io(`${SOCKET_URL}/tracking`, {
    auth: { token },
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 3000,
    transports: ["polling", "websocket"],
  }) as TrackingSocket;

  trackingSocket.on("connect", () => {
    console.log("[Socket] Connected to /tracking:", trackingSocket?.id);
  });
  trackingSocket.on("disconnect", (reason) => {
    console.log("[Socket] Disconnected from /tracking:", reason);
  });
  trackingSocket.on("connect_error", (err) => {
    console.error("[Socket] /tracking error:", err.message);
  });

  return trackingSocket;
}

export function disconnectTrackingSocket(): void {
  if (trackingSocket) {
    trackingSocket.disconnect();
    trackingSocket = null;
  }
}

export function getTrackingSocket(): TrackingSocket | null {
  return trackingSocket;
}

// ─── Notification Socket ──────────────────────────────────────────────────────
let notificationSocket: NotifSocket | null = null;

export function connectNotificationSocket(token: string): NotifSocket {
  if (notificationSocket?.connected) return notificationSocket;

  notificationSocket = io(`${SOCKET_URL}/notifications`, {
    auth: { token },
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 3000,
    transports: ["polling", "websocket"],
  }) as NotifSocket;

  notificationSocket.on("connect", () => {
    console.log(
      "[Socket] Connected to /notifications:",
      notificationSocket?.id,
    );
  });
  notificationSocket.on("disconnect", (reason) => {
    console.log("[Socket] Disconnected from /notifications:", reason);
  });
  notificationSocket.on("connect_error", (err) => {
    console.error("[Socket] /notifications error:", err.message);
  });

  return notificationSocket;
}

export function disconnectNotificationSocket(): void {
  if (notificationSocket) {
    notificationSocket.disconnect();
    notificationSocket = null;
  }
}

export function getNotificationSocket(): NotifSocket | null {
  return notificationSocket;
}

// Day 46 complete
